import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { useAppSettings } from "../contexts/AppSettingsContext";
import { DynamicIcon } from "../components/shared/IconMap";
import {
  AlertTriangle, Bell, FolderSearch,
  HardDrive, CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp,
  Download, Mail, Loader2, Upload, Database, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

export default function DashboardPage({ onLogout, userRole, isFeatureEnabled }) {
  const { settings } = useAppSettings();
  const [config, setConfig] = useState(null);
  const [widgetData, setWidgetData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [scanReport, setScanReport] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportExpanded, setReportExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [woStats, setWoStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [configResp, widgetResp, notifResp] = await Promise.allSettled([
        axios.get(`${API}/dashboard/config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/dashboard/widget-data`, { headers: getAuthHeaders() }),
        axios.get(`${API}/notifications/check`, { headers: getAuthHeaders() }),
      ]);
      if (configResp.status === "fulfilled") setConfig(configResp.value.data);
      else setConfig({ tabs: [{ id: "general", label: "General", description: "Full system overview" }] });
      if (widgetResp.status === "fulfilled") setWidgetData(widgetResp.value.data);
      if (notifResp.status === "fulfilled") setNotifications(notifResp.value.data.notifications || []);
      if (userRole === "admin" || userRole === "superintendent") {
        axios.get(`${API}/scan-report`, { headers: getAuthHeaders() }).then(r => setScanReport(r.data)).catch(() => {});
      }
      axios.get(`${API}/dashboard/analytics`, { headers: getAuthHeaders() }).then(r => setAnalytics(r.data)).catch(() => {});
      axios.get(`${API}/workorders/stats/summary`, { headers: getAuthHeaders() }).then(r => setWoStats(r.data)).catch(() => {});
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !config) {
    return (
      <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
        <div className="flex items-center justify-center h-64">
          <div className="text-[#64748B] font-mono">Loading dashboard...</div>
        </div>
      </Layout>
    );
  }

  const tabs = config?.tabs || [{ id: "general", key: "general", label: "General", description: "Full system overview" }];
  const kpis = config?.kpis || [];
  const panels = config?.panels || [];
  const bp_type_colors = config?.bp_type_colors || {};
  const currentTab = tabs.find(t => t.key === activeTab || t.id === activeTab) || tabs[0];

  // Resolve KPI values from widget-data
  const resolveKpiValue = (kpi) => {
    if (!widgetData) return 0;
    const src = kpi.source;
    // Special handling for blueprints — filter by active tab
    if (src === "total_blueprints") {
      if (activeTab === "general") return widgetData.total_blueprints || 0;
      const bpTypes = widgetData.blueprint_by_type || {};
      const filter = currentTab.bp_filter;
      if (filter === "none") return 0;
      if (Array.isArray(filter)) return filter.reduce((sum, k) => sum + (bpTypes[k] || 0), 0);
      return widgetData.total_blueprints || 0;
    }
    // Direct lookup from widget-data
    if (src in widgetData) return widgetData[src];
    return 0;
  };

  // Dynamic KPI color (alerts turn green when 0)
  const resolveKpiColor = (kpi, value) => {
    if (kpi.key === "alerts") return value > 0 ? "#DC2626" : "#10B981";
    return kpi.color;
  };

  // Filter blueprint types by active tab
  const allBpTypes = widgetData?.blueprint_by_type || {};
  const filteredBpTypes = (() => {
    const filter = currentTab.bp_filter;
    if (filter === "none") return {};
    if (filter === "all") return allBpTypes;
    if (Array.isArray(filter)) {
      const result = {};
      filter.forEach(k => { if (allBpTypes[k] !== undefined) result[k] = allBpTypes[k]; });
      if (activeTab === "general" && allBpTypes.uncategorized !== undefined) result.uncategorized = allBpTypes.uncategorized;
      return result;
    }
    return allBpTypes;
  })();

  // Filter notifications by tab
  const facilityTypeSet = new Set(["facilities_electrical", "facilities_structural"]);
  const productionTypeSet = new Set(["mechanical", "electrical", "hydraulic"]);
  const filteredNotifications = activeTab === "general" ? notifications
    : activeTab === "contractors" ? notifications.filter(n => n.type === "overdue_maintenance" || n.type === "workorder")
    : notifications.filter(n => {
        if (n.missing_types) {
          const typeSet = activeTab === "facilities" ? facilityTypeSet : productionTypeSet;
          return n.missing_types.some(t => typeSet.has(t));
        }
        return true;
      });

  const highNotifs = filteredNotifications.filter(n => n.severity === "high");

  // Panel visibility check
  const shouldShowPanel = (panel) => {
    if (panel.roles && !panel.roles.includes(userRole)) return false;
    if (panel.show_on && !panel.show_on.includes(activeTab)) return false;
    return true;
  };

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="dashboard">
        <div>
          <h1 className="font-heading font-black text-4xl text-[#0F172A] mb-2" data-testid="dashboard-title">
            System Overview
          </h1>
          <p className="text-[#64748B] text-sm">Enterprise Operational Coordination Platform</p>
        </div>

        {/* Dynamic Tabs */}
        <div className="flex border-b-2 border-[#E2E8F0]" data-testid="dashboard-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-6 py-3 text-sm font-bold tracking-wide transition-colors relative ${
                activeTab === t.key ? "text-[#D97706]" : "text-[#78716C] hover:text-[#1C1917]"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label.toUpperCase()}
              {activeTab === t.key && <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#D97706]" />}
            </button>
          ))}
        </div>
        <p className="text-xs text-[#78716C]">{currentTab.desc}</p>

        {/* Critical Alert Banner */}
        {highNotifs.length > 0 && (
          <div className="bg-[#DC2626] border-2 border-[#DC2626] p-4 flex items-start gap-3" data-testid="critical-alert-banner">
            <AlertTriangle className="text-white mt-0.5 shrink-0" size={20} />
            <div>
              <h3 className="text-white font-bold tracking-wide">
                {highNotifs.length} CRITICAL ALERT{highNotifs.length > 1 ? "S" : ""}
              </h3>
              <p className="text-white/90 text-sm mt-1">
                Missing blueprints, flagged documents, or overdue maintenance detected. Review notifications below.
              </p>
            </div>
          </div>
        )}

        {/* Dynamic KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {kpis.map(kpi => {
            const value = resolveKpiValue(kpi);
            const color = resolveKpiColor(kpi, value);
            return (
              <div key={kpi.key} className="bg-white border border-[#E2E8F0] p-6 transition-all duration-150 hover:border-[#0F172A]" data-testid={`stat-card-${kpi.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div style={{ color }}><DynamicIcon name={kpi.icon} size={24} /></div>
                </div>
                <div className="font-mono text-3xl font-bold text-[#0F172A]" data-testid={`stat-value-${kpi.key}`}>{value}</div>
                <div className="text-[#64748B] text-sm mt-1 tracking-wide">
                  {activeTab !== "general" && kpi.key === "total_blueprints" ? `${currentTab.label} Blueprints` : kpi.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Dynamic Panel Renderer */}
        {panels.map(panel => {
          if (!shouldShowPanel(panel)) return null;
          return <PanelRenderer key={panel.type} panel={panel} config={config} widgetData={widgetData} notifications={filteredNotifications} scanReport={scanReport} analytics={analytics} woStats={woStats} reportExpanded={reportExpanded} setReportExpanded={setReportExpanded} fetchData={fetchData} userRole={userRole} activeTab={activeTab} filteredBpTypes={filteredBpTypes} currentTab={currentTab} />;
        })}

        {/* Credit */}
        <div className="text-center pt-4 border-t border-[#E2E8F0]">
          <p className="text-xs text-[#94A3B8] tracking-wide" data-testid="designed-by">Engineered & Built by Robert J Corn 2026</p>
        </div>
      </div>
    </Layout>
  );
}

function PanelRenderer({ panel, config, widgetData, notifications, scanReport, analytics, woStats, reportExpanded, setReportExpanded, fetchData, userRole, activeTab, filteredBpTypes, currentTab }) {
  switch (panel.type) {
    case "blueprint_distribution":
      return <BlueprintDistributionPanel config={config} filteredBpTypes={filteredBpTypes} activeTab={activeTab} currentTab={currentTab} />;
    case "contractor_work_orders":
      return <ContractorPanel woStats={woStats} />;
    case "scan_report":
      return scanReport ? <ScanReportPanel report={scanReport} expanded={reportExpanded} onToggle={() => setReportExpanded(!reportExpanded)} onRefresh={fetchData} /> : null;
    case "notifications":
      return <NotificationsPanel notifications={notifications} fetchData={fetchData} userRole={userRole} />;
    case "backup_restore":
      return <BackupRestorePanel fetchData={fetchData} />;
    case "analytics":
      return analytics ? <AnalyticsPanel analytics={analytics} /> : null;
    default:
      return null;
  }
}

function BlueprintDistributionPanel({ config, filteredBpTypes, activeTab, currentTab }) {
  const colors = config.bp_type_colors || {};
  const entries = Object.entries(filteredBpTypes);
  if (entries.length === 0) return null;
  return (
    <div className="bg-white border border-[#E2E8F0] p-6">
      <h2 className="font-heading font-bold text-lg text-[#0F172A] mb-4" data-testid="blueprint-distribution-title">
        {activeTab === "general" ? "Blueprint Distribution by Type" : `${currentTab.label} Blueprint Distribution`}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {entries.map(([key, count]) => {
          const meta = colors[key] || { label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), color: "#64748B" };
          return (
            <div key={key} className="border border-[#E2E8F0] p-4">
              <div className="w-3 h-3 rounded-full mb-2" style={{ backgroundColor: meta.color }} />
              <div className="font-mono text-2xl font-bold text-[#0F172A]">{count}</div>
              <div className="text-[#64748B] text-xs mt-1 tracking-wide uppercase">{meta.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContractorPanel({ woStats }) {
  const items = [
    { key: "submitted", label: "Awaiting Approval", color: "#D97706" },
    { key: "in_progress", label: "In Progress", color: "#2563EB" },
    { key: "completed", label: "Completed", color: "#10B981" },
  ];
  return (
    <div className="bg-white border border-[#E2E8F0] p-6" data-testid="contractors-panel">
      <h2 className="font-heading font-bold text-lg text-[#0F172A] mb-4">Contractor Work Orders</h2>
      <p className="text-sm text-[#64748B] mb-4">Contractor-assigned work orders, external maintenance tasks, and pending approvals.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map(i => (
          <div key={i.key} className="border border-[#E2E8F0] p-4 text-center">
            <div className="text-3xl font-mono font-black" style={{ color: i.color }}>{woStats?.by_status?.[i.key] || 0}</div>
            <div className="text-xs text-[#64748B] uppercase tracking-wider font-bold mt-1">{i.label}</div>
          </div>
        ))}
        <div className="border border-[#E2E8F0] p-4 text-center">
          <div className="text-3xl font-mono font-black text-[#DC2626]">{woStats?.overdue || 0}</div>
          <div className="text-xs text-[#64748B] uppercase tracking-wider font-bold mt-1">Overdue</div>
        </div>
      </div>
    </div>
  );
}

function NotificationsPanel({ notifications, fetchData, userRole }) {
  return (
    <div className="bg-white border border-[#E2E8F0]" data-testid="notifications-panel">
      <div className="p-4 border-b border-[#E2E8F0] flex items-center gap-2">
        <Bell size={18} className="text-[#0F172A]" />
        <h2 className="font-heading font-bold text-lg text-[#0F172A]">Notifications</h2>
        <span className="ml-auto text-xs font-mono text-[#64748B]">{notifications.length} alert{notifications.length !== 1 ? "s" : ""}</span>
        {(userRole === "admin" || userRole === "superintendent") && (
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              const resp = await axios.post(`${API}/maintenance/check-overdue`, {}, { headers: getAuthHeaders() });
              toast.success(resp.data.message);
              fetchData();
            } catch { toast.error("Overdue check failed"); }
          }} className="rounded-none border-[#E2E8F0] text-xs ml-2" data-testid="check-overdue-button">
            <AlertTriangle size={14} className="mr-1" />Check Overdue
          </Button>
        )}
      </div>
      {notifications.length === 0 ? (
        <div className="p-8 text-center">
          <FolderSearch size={36} className="mx-auto text-[#10B981] mb-3" />
          <p className="text-[#10B981] font-medium">All Clear</p>
          <p className="text-[#64748B] text-sm mt-1">No missing blueprints or overdue items detected.</p>
        </div>
      ) : (
        <div className="divide-y divide-[#F1F5F9]">
          {notifications.map((notif, idx) => <NotificationRow key={idx} notif={notif} />)}
        </div>
      )}
    </div>
  );
}

function BackupRestorePanel({ fetchData }) {
  return (
    <div className="bg-white border border-[#E2E8F0]" data-testid="backup-restore-panel">
      <div className="p-4 border-b border-[#E2E8F0] flex items-center gap-2">
        <Database size={18} className="text-[#0F172A]" />
        <h2 className="font-heading font-bold text-lg text-[#0F172A]">Database Backup / Restore</h2>
      </div>
      <div className="p-4 flex gap-4 items-center">
        <Button variant="outline" onClick={async () => {
          try {
            const resp = await axios.get(`${API}/admin/backup`, { headers: getAuthHeaders(), responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `blueprint_ai_backup_${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
          } catch { toast.error("Backup failed"); }
        }} className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white" data-testid="download-backup-button">
          <Download size={14} className="mr-2" /> Download Backup
        </Button>
        <div className="relative">
          <input type="file" accept=".json" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (!window.confirm("Restore from backup? This will merge data into the existing database.")) return;
            const formData = new FormData();
            formData.append("file", file);
            try {
              const resp = await axios.post(`${API}/admin/restore`, formData, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
              toast.success(resp.data.message);
              fetchData();
            } catch { toast.error("Restore failed"); }
            e.target.value = "";
          }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="restore-file-input" />
          <Button variant="outline" className="rounded-none border-[#EAB308] text-[#EAB308] hover:bg-[#EAB308] hover:text-white pointer-events-none" tabIndex={-1}>
            <Upload size={14} className="mr-2" /> Restore from Backup
          </Button>
        </div>
        <p className="text-xs text-[#64748B]">Backup includes all collections. Restore merges without deleting existing data.</p>
      </div>
    </div>
  );
}

function AnalyticsPanel({ analytics }) {
  return (
    <div className="space-y-4" data-testid="analytics-section">
      <h2 className="font-heading font-bold text-lg text-[#0F172A] flex items-center gap-2">
        <Activity size={18} className="text-[#2563EB]" /> Analytics
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {analytics.upload_activity?.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-4" data-testid="chart-uploads">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">Monthly Upload Activity</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analytics.upload_activity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="uploads" fill="#2563EB" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {analytics.maintenance_stats?.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-4" data-testid="chart-maintenance">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">Maintenance Completion Rate</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={analytics.maintenance_stats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="rate" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {analytics.blueprint_types?.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-4" data-testid="chart-blueprint-types">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">Blueprints by Type</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={analytics.blueprint_types} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} label={({ type, count }) => `${type} (${count})`}>
                  {analytics.blueprint_types.map((_, i) => (
                    <Cell key={i} fill={["#2563EB", "#10B981", "#F59E0B", "#DC2626", "#8B5CF6", "#06B6D4"][i % 6]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        {analytics.machine_statuses?.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-4" data-testid="chart-machine-status">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">Machine Status Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analytics.machine_statuses} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="status" type="category" tick={{ fontSize: 10 }} width={80} />
                <Tooltip />
                <Bar dataKey="count" fill="#0F172A" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function ScanReportPanel({ report, expanded, onToggle, onRefresh }) {
  const s = report.summary;
  const hasIssues = s.failed_scans > 0 || s.total_errors > 0;
  const hasPending = s.pending_review > 0;
  const [importing, setImporting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [sendingEmail, setSendingEmail] = useState(false);

  const toggleFile = (filePath) => {
    setSelectedFiles(prev => { const next = new Set(prev); if (next.has(filePath)) next.delete(filePath); else next.add(filePath); return next; });
  };
  const toggleAllFiles = () => {
    if (selectedFiles.size === report.pending_files.length) setSelectedFiles(new Set());
    else setSelectedFiles(new Set(report.pending_files.map(f => f.file_path)));
  };
  const handleBulkImport = async () => {
    if (selectedFiles.size === 0) return;
    setImporting(true);
    try {
      const resp = await axios.post(`${API}/scan-report/bulk-import`, Array.from(selectedFiles), { headers: getAuthHeaders() });
      if (resp.data.imported_count > 0) toast.success(`Imported ${resp.data.imported_count} file(s)`);
      if (resp.data.error_count > 0) toast.error(`${resp.data.error_count} file(s) failed`);
      setSelectedFiles(new Set());
      if (onRefresh) onRefresh();
    } catch { toast.error("Bulk import failed"); }
    finally { setImporting(false); }
  };
  const handleSendReportEmail = async () => {
    setSendingEmail(true);
    try {
      const resp = await axios.post(`${API}/notifications/send-report-email`, {}, { headers: getAuthHeaders() });
      toast.success(resp.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send report email"); }
    finally { setSendingEmail(false); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0]" data-testid="scan-report-panel">
      <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
        <button onClick={onToggle} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="scan-report-toggle">
          <HardDrive size={18} className="text-[#2563EB]" />
          <h2 className="font-heading font-bold text-lg text-[#0F172A]">Scan Report</h2>
          <span className="text-xs text-[#64748B] font-mono ml-1">{report.period}</span>
          {hasIssues && <span className="ml-2 px-2 py-0.5 bg-[#DC2626] text-white text-xs font-bold">ISSUES</span>}
          {hasPending && !hasIssues && <span className="ml-2 px-2 py-0.5 bg-[#EAB308] text-white text-xs font-bold">REVIEW</span>}
          {expanded ? <ChevronUp size={18} className="text-[#64748B]" /> : <ChevronDown size={18} className="text-[#64748B]" />}
        </button>
        <Button size="sm" variant="outline" onClick={handleSendReportEmail} disabled={sendingEmail} className="rounded-none border-[#E2E8F0] text-sm" data-testid="email-report-button">
          {sendingEmail ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />}
          Email Report
        </Button>
      </div>
      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 border-b border-[#F1F5F9] bg-[#FAFAFA]">
        <ReportStat label="Scheduled Scans" value={s.total_scheduled_scans} />
        <ReportStat label="Manual Scans" value={s.total_manual_scans} />
        <ReportStat label="Files Found" value={s.total_files_found} color="#2563EB" />
        <ReportStat label="Imported" value={s.total_files_imported} color="#10B981" />
        <ReportStat label="Pending Review" value={s.pending_review} color={s.pending_review > 0 ? "#EAB308" : undefined} />
        <ReportStat label="Errors" value={s.total_errors} color={s.total_errors > 0 ? "#DC2626" : undefined} />
        <ReportStat label="Failed Scans" value={s.failed_scans} color={s.failed_scans > 0 ? "#DC2626" : undefined} />
      </div>
      {expanded && (
        <div className="divide-y divide-[#F1F5F9]">
          {report.scheduled_paths.length > 0 && (
            <div className="p-4">
              <h3 className="text-xs font-bold text-[#64748B] uppercase tracking-wide mb-3">Scheduled Path Status</h3>
              <div className="space-y-2">
                {report.scheduled_paths.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#FAFAFA] border border-[#E2E8F0]" data-testid={`report-path-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {p.last_status === "completed" ? <CheckCircle2 size={14} className="text-[#10B981] shrink-0" /> : p.last_status === "failed" ? <XCircle size={14} className="text-[#DC2626] shrink-0" /> : <Clock size={14} className="text-[#64748B] shrink-0" />}
                      <span className="font-mono text-sm text-[#0F172A] truncate">{p.path}</span>
                      {p.label && <span className="text-xs text-[#64748B]">({p.label})</span>}
                      {!p.enabled && <span className="text-xs text-[#94A3B8] font-bold">DISABLED</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#64748B] font-mono shrink-0">
                      <span>{p.last_files_found} files</span>
                      {p.last_errors > 0 && <span className="text-[#DC2626]">{p.last_errors} errors</span>}
                      {p.last_scan_at && <span>{new Date(p.last_scan_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {report.failed_scan_details.length > 0 && (
            <div className="p-4">
              <h3 className="text-xs font-bold text-[#DC2626] uppercase tracking-wide mb-3">Failed Scans</h3>
              <div className="space-y-2">
                {report.failed_scan_details.map((f, i) => (
                  <div key={i} className="py-2 px-3 bg-[#FEF2F2] border border-[#FECACA] text-sm">
                    <div className="font-mono text-[#0F172A]">{f.scan_path}</div>
                    <div className="text-[#DC2626] text-xs mt-1">{f.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {report.pending_files.length > 0 && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-[#EAB308] uppercase tracking-wide">Files Pending Review ({report.pending_files.length})</h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer">
                    <input type="checkbox" checked={selectedFiles.size === report.pending_files.length && report.pending_files.length > 0} onChange={toggleAllFiles} className="rounded border-[#CBD5E1]" data-testid="select-all-pending" />
                    Select all
                  </label>
                  {selectedFiles.size > 0 && (
                    <Button size="sm" onClick={handleBulkImport} disabled={importing} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none text-xs" data-testid="bulk-import-button">
                      {importing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Download size={12} className="mr-1" />}
                      Import {selectedFiles.size} file(s)
                    </Button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-files-table">
                  <thead>
                    <tr className="border-b border-[#E2E8F0] text-left">
                      <th className="pb-2 pr-2 w-8"></th>
                      <th className="pb-2 pr-3 text-[#64748B] text-xs font-bold">File</th>
                      <th className="pb-2 pr-3 text-[#64748B] text-xs font-bold">Type</th>
                      <th className="pb-2 pr-3 text-[#64748B] text-xs font-bold">Machine</th>
                      <th className="pb-2 pr-3 text-[#64748B] text-xs font-bold">Source</th>
                      <th className="pb-2 text-[#64748B] text-xs font-bold">Scanned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.pending_files.slice(0, 20).map((f, i) => {
                      const typeColors = { mechanical: "#2563EB", electrical: "#EAB308", hydraulic: "#10B981", facilities_electrical: "#8B5CF6", facilities_structural: "#F97316" };
                      return (
                        <tr key={i} className="border-b border-[#F1F5F9]" data-testid={`pending-file-${i}`}>
                          <td className="py-2 pr-2"><input type="checkbox" checked={selectedFiles.has(f.file_path)} onChange={() => toggleFile(f.file_path)} className="rounded border-[#CBD5E1]" /></td>
                          <td className="py-2 pr-3 font-mono text-xs text-[#0F172A]">{f.file_name}</td>
                          <td className="py-2 pr-3">{f.blueprint_type ? <span className="text-xs font-bold uppercase" style={{ color: typeColors[f.blueprint_type] || "#64748B" }}>{f.blueprint_type.replace("_", " ")}</span> : <span className="text-xs text-[#94A3B8]">--</span>}</td>
                          <td className="py-2 pr-3 font-mono text-xs text-[#0F172A]">{f.machine_number || "--"}</td>
                          <td className="py-2 pr-3 text-xs text-[#64748B] truncate max-w-[200px]" title={f.scan_path}>{f.scan_path}</td>
                          <td className="py-2 text-xs text-[#64748B]">{f.scan_date ? new Date(f.scan_date).toLocaleDateString() : "--"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {report.pending_files.length > 20 && <p className="text-xs text-[#64748B] mt-2">Showing 20 of {report.pending_files.length} files.</p>}
              </div>
            </div>
          )}
          {report.pending_files.length === 0 && report.failed_scan_details.length === 0 && (
            <div className="p-6 text-center">
              <CheckCircle2 size={24} className="mx-auto text-[#10B981] mb-2" />
              <p className="text-[#10B981] text-sm font-medium">No issues to report</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportStat({ label, value, color }) {
  return (
    <div className="text-center">
      <div className="font-mono text-xl font-bold" style={{ color: color || "#0F172A" }}>{value}</div>
      <div className="text-[#64748B] text-xs mt-0.5">{label}</div>
    </div>
  );
}

function NotificationRow({ notif }) {
  const severityConfig = {
    high: { bg: "bg-[#FEF2F2]", border: "border-l-[#DC2626]", icon: <AlertTriangle size={16} className="text-[#DC2626]" /> },
    medium: { bg: "bg-[#FFFBEB]", border: "border-l-[#EAB308]", icon: <AlertTriangle size={16} className="text-[#EAB308]" /> },
    low: { bg: "bg-[#F0FDF4]", border: "border-l-[#10B981]", icon: <Bell size={16} className="text-[#10B981]" /> },
  };
  const cfg = severityConfig[notif.severity] || severityConfig.low;
  return (
    <div className={`px-4 py-3 flex items-start gap-3 border-l-4 ${cfg.bg} ${cfg.border}`} data-testid={`notification-${notif.type}-${notif.machine_number || "general"}`}>
      <div className="mt-0.5 shrink-0">{cfg.icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#0F172A]">{notif.message}</p>
        {notif.machine_name && <p className="text-xs text-[#64748B] mt-0.5">{notif.machine_name}</p>}
      </div>
      <span className="text-xs font-bold uppercase tracking-wide shrink-0" style={{ color: notif.severity === "high" ? "#DC2626" : notif.severity === "medium" ? "#EAB308" : "#10B981" }}>{notif.severity}</span>
    </div>
  );
}
